import { CalendarEvent } from "../types/event";
import { formatDayHeading, toDayKey } from "./eventDates";

/**
 * "Start at this day", as a request rather than a value.
 *
 * `seq` is what makes pressing Today a second time work. Without it the only
 * state is the date, which does not change between presses — so having scrolled
 * away, asking again would be a no-op. The window remembers the last `seq` it
 * acted on instead of the last day.
 *
 * It PLACES the window at that day rather than scrolling to it, which is the
 * only version of this that stays quick. A month here is not a handful of events
 * — August 2026 holds 8,072 — so today is routinely a few thousand rows down,
 * and scrolling to it would mean mounting every row above it first. The days
 * before it are not lost: scrolling up walks back into them.
 */
export interface DayFocus {
	/** Local `YYYY-MM-DD`. */
	day: string;
	/**
	 * Which request this is.
	 *
	 * THE COUNTER MUST BE SHARED by everything that can ask, and must never
	 * restart. `useDayWindow` remembers the last seq it acted on, so two sources
	 * counting independently will eventually both ask with the same number and
	 * the second request is read as one already handled — silently, and only
	 * where a window is already placed. See EventList's `nextFocusSeq`.
	 */
	seq: number;
	/**
	 * Place at the day NEAREST the one asked for, in either direction, rather
	 * than at the first one on or after it.
	 *
	 * The default suits Today, where the question is "what is coming up". A
	 * search asks a different one: its results can sit entirely in the past, or
	 * resume months out, and opening six months forward when the last matching
	 * event was on Friday is not the closest answer to today.
	 */
	nearest?: boolean;
}

/** One day's events, in start order. */
export interface DayGroup {
	/** Local `YYYY-MM-DD`, or `unscheduled`. Stable across refetches. */
	key: string;
	heading: string;
	events: CalendarEvent[];
}

/**
 * Sort by start, then group into days.
 *
 * Shared by both browse views: the List draws the headings, and the Cards grid
 * uses the same grouping to window itself, so that scrolling either one moves
 * through time at the same rate.
 *
 * A multi-day event appears ONCE, under the day it starts. Repeating it under
 * every day it spans is what a calendar grid is for; in a list it would make a
 * three-week excursion the loudest thing on the page. The day count in each
 * heading therefore counts events STARTING that day, which is what the heading
 * says.
 *
 * Events with no usable start sort last under an "Unscheduled" heading rather
 * than being dropped — an event with a broken date is exactly the one someone
 * is looking for.
 */
export function groupByDay(events: CalendarEvent[]): DayGroup[] {
	const groups = new Map<string, DayGroup>();

	const sorted = [...events].sort((a, b) => {
		const at = new Date(a.start ?? "").getTime();
		const bt = new Date(b.start ?? "").getTime();
		if (Number.isNaN(at) && Number.isNaN(bt)) return 0;
		if (Number.isNaN(at)) return 1;
		if (Number.isNaN(bt)) return -1;
		return at - bt;
	});

	for (const evt of sorted) {
		const start = new Date(evt.start ?? "");
		const valid = !Number.isNaN(start.getTime());
		const key = valid ? toDayKey(start) : "unscheduled";
		let group = groups.get(key);
		if (!group) {
			group = {
				key,
				heading: valid ? formatDayHeading(start) : "Unscheduled",
				events: [],
			};
			groups.set(key, group);
		}
		group.events.push(evt);
	}

	return [...groups.values()];
}
